import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthContext";
import { i18n } from "../../localization/i18n";
import api from "../../services/api";
import {
  Container,
  MainChartContainer,
  ChartArea,
  ChartTitle,
  TopLayoutGrid,
  MainMetricsCard,
  SideMetricsGrid,
  MainMetricsInfo,
  MainMetricsChart,
  ChartTitleRow,
  ViewAllLink,
  LeftColumnContainer,
  MobileActionsWrapper,
  DesktopActionsWrapper,
  MainMetricTextContent
} from "./styles";
import { GiWireCoil } from "react-icons/gi";
import { NumberCardBox } from "./components/NumberCardBox";
import { FiUsers, FiArrowRight } from "react-icons/fi";
import { MdOutlineAttachMoney } from "react-icons/md";
import { RiShoppingCart2Line } from "react-icons/ri";
import { IFilter } from "../Orders/dtos";
import { startOfYear, endOfYear } from "date-fns";
import { DateDropdownFilter } from "../../components/DateDropdownFilter";
import { showToast } from "../../components/CustomToast";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../components/PrimaryButton";
import { Skeleton } from "@mui/material";
import Chart from "react-apexcharts";
import { roles } from "../../utils/constants";

export interface IReportResponse {
  day: number;
  lastSevenDays: number[];
  yearByMonth: number[];
  ordersCount: number;
  patientsLeadsCount: number;
  filamentMaterialConsumption: number | null;
  revenueGenerated: number;
}

export function Dashboard() {
  const navigate = useNavigate();
  const { setNavigationAction, user } = useAuth();

  const [language, setLanguage] = useState("pt-BR");
    const [filter, setFilter] = useState<IFilter>({
    period: { from: startOfYear(new Date()), to: endOfYear(new Date()) },
  });
  const [dashboardData, setDashboardData] = useState<IReportResponse>();
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    async function getDashboardData() {
      setLoading(true);
      const queryList = [];

      queryList.push(`language=${language}`);

      if (filter?.period?.from !== undefined && filter?.period?.to !== undefined) {
        queryList.push(`from=${filter.period.from.toJSON()}`);
        queryList.push(`to=${filter.period.to.toJSON()}`);
      }

      try {
        const response = await api.get(`/reports?${queryList.join("&")}`);
        setDashboardData(response.data);
      } catch (error) {
        showToast({
          type: "error",
          message: `${i18n.t("dashboard.error")}`,
        });
      } finally {
        setLoading(false);
      }
    }

    if (filter?.period?.from !== undefined) {
      getDashboardData();
    }
  }, [filter, language]);

  useEffect(() => {
    const currentLanguage = localStorage.getItem("i18nextLng");
    if (currentLanguage === null) {
      return;
    } else if (currentLanguage.startsWith("pt")) {
      setLanguage("pt-BR");
    } else if (currentLanguage.startsWith("es")) {
      setLanguage("es");
    } else if (currentLanguage.startsWith("en")) {
      setLanguage("en");
    }
  }, []);

  useEffect(() => {
    setNavigationAction({
      hasButton: false,
      description: `Olá, ${user?.person?.name?.split(' ')[0] || (user?.role === roles.admin ? "Admin" : "Licenciado")}`,
      customElement: (
        <DesktopActionsWrapper>
          <DateDropdownFilter
            defaultOption="this_year"
            defaultPeriod={{ from: startOfYear(new Date()), to: endOfYear(new Date()) }}
            onApply={(period) => {
              setFilter({ ...filter, period });
            }}
          />
          {user?.role !== roles.admin && (
            <PrimaryButton 
              text="Novo Pedido" 
              showPlusIcon 
              onClick={() => navigate("/solutions")} 
              style={{ height: '38px', borderRadius: '20px', whiteSpace: 'nowrap' }}
            />
          )}
        </DesktopActionsWrapper>
      )
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  const optionsYearByMonth = {
    colors: ["#9835D1"],
    labels: [
      i18n.t("dashboard.months.jan"),
      i18n.t("dashboard.months.feb"),
      i18n.t("dashboard.months.mar"),
      i18n.t("dashboard.months.apr"),
      i18n.t("dashboard.months.may"),
      i18n.t("dashboard.months.jun"),
      i18n.t("dashboard.months.jul"),
      i18n.t("dashboard.months.aug"),
      i18n.t("dashboard.months.sep"),
      i18n.t("dashboard.months.oct"),
      i18n.t("dashboard.months.nov"),
      i18n.t("dashboard.months.dec"),
    ],
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      foreColor: "var(--gray)",
      fontFamily: "inherit",
    },
    grid: {
      show: true,
      borderColor: 'rgba(0,0,0,0.05)',
      strokeDashArray: 4,
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: undefined,
      },
      background: {
        enabled: true,
        borderColor: undefined,
        dropShadow: {
          enabled: false,
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '50%',
      }
    },
    yaxis: {
      show: true,
    },
  };

  const seriesYearByMonth = [{ name: "Pedidos", data: dashboardData?.yearByMonth || [] }];

  const currentYear = new Date().getFullYear();

  let lastSevenDaysLabel = [] as string[];

  for (
    let removeDaysQuantity = 1;
    removeDaysQuantity <= 7;
    removeDaysQuantity++
  ) {
    const date = new Date(
      new Date().setDate(new Date().getDate() - removeDaysQuantity)
    );
    const dateFormatted = date.toLocaleDateString(language, {
      day: "2-digit",
      month: "2-digit",
    });
    lastSevenDaysLabel.push(dateFormatted);
  }

  // Reverse labels and data to show oldest to newest (left to right)
  const chartLabels = [...lastSevenDaysLabel];
  const reportData = dashboardData?.lastSevenDays ? [...dashboardData.lastSevenDays] : [];

  const optionsLastSevenDays = {
    colors: ["#9835D1"],
    labels: chartLabels,
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      foreColor: "var(--gray)",
      fontFamily: "inherit"
    },
    grid: {
      show: false,
      padding: {
        top: 0,
        bottom: -15,
        left: 40,
        right: 40
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: undefined,
      },
      background: {
        enabled: true,
        borderColor: undefined,
        dropShadow: {
          enabled: false,
        },
      },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    fill: {
      type: 'gradient' as const,
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      labels: { show: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
      min: 0,
      max: 5,
      tickAmount: 5,
    },
  };

  const seriesLastSevenDays = [
    { name: i18n.t("dashboard.requestsSevenDays") || "Pedidos", data: reportData },
  ];

  return (
    <Container>
      <TopLayoutGrid>
          <LeftColumnContainer>
          <MainMetricsCard>
            <MainMetricsInfo>
              <MainMetricTextContent>
                <ChartTitleRow>
                  <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
                    Pedidos hoje
                  </ChartTitle>
                  {user?.role !== roles.admin && (
                    <ViewAllLink onClick={() => navigate("/orders")}>
                      Ver todos <FiArrowRight />
                    </ViewAllLink>
                  )}
                </ChartTitleRow>
                
                <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--fixit)', lineHeight: 1 }}>
                  {loading ? (
                    <Skeleton variant="text" width={60} height={48} />
                  ) : (
                    dashboardData?.day ?? 0
                  )}
                </div>
              </MainMetricTextContent>
            </MainMetricsInfo>
            
            <MainMetricsChart>
              <ChartTitleRow style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
                  {`${i18n.t("dashboard.requestsSevenDays")}`}
                </ChartTitle>
              </ChartTitleRow>
              {loading ? (
                <Skeleton variant="rounded" width="100%" height={120} sx={{ margin: '0 24px', width: 'calc(100% - 48px)' }} />
              ) : (
                <Chart
                  options={optionsLastSevenDays}
                  series={seriesLastSevenDays}
                  type="area"
                  height={120}
                />
              )}
            </MainMetricsChart>
          </MainMetricsCard>

          <MobileActionsWrapper>
            {user?.role !== roles.admin && (
              <PrimaryButton 
                text="Novo Pedido" 
                showPlusIcon 
                onClick={() => navigate("/solutions")} 
                style={{ height: '38px', borderRadius: '20px' }}
              />
            )}
            <DateDropdownFilter
              onApply={(period) => {
                setFilter({ ...filter, period });
              }}
            />
          </MobileActionsWrapper>
        </LeftColumnContainer>

        <SideMetricsGrid>
          <NumberCardBox
            icon={<RiShoppingCart2Line />}
            title="Pedidos"
            value={dashboardData?.ordersCount ?? 0}
            isLoading={loading}
          />
          <NumberCardBox
            icon={<MdOutlineAttachMoney />}
            title="Retorno"
            value={Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(dashboardData?.revenueGenerated ?? 0)}
            hasAditionalInfo={true}
            aditionalInfo="Calculado com base na tabela sugerida"
            isLoading={loading}
          />
          <NumberCardBox
            icon={<FiUsers />}
            title="Pacientes pelo Localizador"
            value={dashboardData?.patientsLeadsCount ?? 0}
            hasAditionalInfo={true}
            aditionalInfo="Pacientes gerados pelo localizador durante período selecionado"
            isLoading={loading}
          />
          <NumberCardBox
            icon={<GiWireCoil />}
            title="Consumo de Filamento"
            value={`${dashboardData?.filamentMaterialConsumption ?? 0}g`}
            hasAditionalInfo={true}
            aditionalInfo="Calculado com base nos pedidos padronizados"
            isLoading={loading}
          />
        </SideMetricsGrid>
      </TopLayoutGrid>

      <MainChartContainer>
        <ChartArea>
          <ChartTitleRow style={{ marginBottom: '8px' }}>
            <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
              Pedidos em {currentYear}
            </ChartTitle>
          </ChartTitleRow>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={280} />
          ) : (
            <Chart
              options={optionsYearByMonth}
              series={seriesYearByMonth}
              type="bar"
              height={280}
            />
          )}
        </ChartArea>
      </MainChartContainer>
    </Container>
  );
}
